import { ConflictSide, FileContent } from '../git.model';

const CONFLICT_START_MARKER = '<<<<<<<';
const CONFLICT_SIDE_SEPARATOR = '=======';
const CONFLICT_END_MARKER = '>>>>>>>';

/**
 * https://git-scm.com/docs/git-merge#_how_conflicts_are_presented
 * Here are lines that are either unchanged from the common
 * ancestor, or cleanly resolved because only one side changed.
 * <<<<<<< yours:sample.txt
 * Conflict resolution is hard;
 * let's go shopping.
 * =======
 * Git makes conflict resolution easy.
 * >>>>>>> theirs:sample.txt
 * And here is another line that is cleanly resolved or unmodified.
 */
function resolveNextConflict(text: string, conflictSide: ConflictSide) {
  const lines = text.split('\n');
  // default merge.conflictStyle=merge
  const conflictYoursIndex = lines.findIndex((l) => l.startsWith(CONFLICT_START_MARKER));
  const conflictTheirsIndex = lines.findIndex((l) => l.startsWith(CONFLICT_SIDE_SEPARATOR));
  const conflictEndIndex = lines.findIndex((l) => l.startsWith(CONFLICT_END_MARKER));
  if (lines[conflictEndIndex]?.endsWith('Stashed changes')) {
    conflictSide = conflictSide === ConflictSide.YOURS ? ConflictSide.THEIRS : ConflictSide.YOURS;
  }

  let chosen: string[];

  switch (conflictSide) {
    case ConflictSide.YOURS:
      chosen = lines.slice(conflictYoursIndex + 1, conflictTheirsIndex);
      break;
    case ConflictSide.THEIRS:
      chosen = lines.slice(conflictTheirsIndex + 1, conflictEndIndex);
      break;
  }

  return lines
    .slice(0, conflictYoursIndex)
    .concat(chosen)
    .concat(lines.slice(conflictEndIndex + 1, lines.length))
    .join('\n');
}

export function contentHasConflicts(content: FileContent): boolean {
  const lines = content.split('\n');
  return lines.some((line) => line.startsWith(CONFLICT_START_MARKER));
}

export function resolveConflict(content: FileContent, conflictSide: ConflictSide) {
  while (contentHasConflicts(content)) {
    content = resolveNextConflict(content, conflictSide);
  }
  return content;
}

export function getConflictLines(content: string) {
  if (!contentHasConflicts(content)) {
    return [];
  }

  const lines = content.split('\n');

  const conflictingLines = [];

  let index = 0;
  let insideSecondDiff = false;
  let startLine = 0;

  for (const line of lines) {
    if (line.startsWith(CONFLICT_START_MARKER)) {
      startLine = index + 1;
    }

    if (line.startsWith(CONFLICT_SIDE_SEPARATOR)) {
      conflictingLines.push({ startLine, endLine: index - 1 });
      insideSecondDiff = true;
    }

    if (line.startsWith(CONFLICT_END_MARKER)) {
      insideSecondDiff = false;
    }

    if (!insideSecondDiff) {
      index++;
    }
  }

  return conflictingLines;
}
